import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/cn';

export interface Crumb {
  label: ReactNode;
  to?: string;
}

/** Page frame inside the main panel. `flush` lets full-bleed tables touch the panel edges. */
export function Page({
  children,
  className,
  flush = false,
}: {
  children: ReactNode;
  className?: string;
  flush?: boolean;
}) {
  return (
    <div className={cn('flex min-h-full animate-page-in flex-col', !flush && 'px-4 pb-8', className)}>
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  eyebrow,
  description,
  breadcrumbs,
  actions,
  meta,
  className,
  children,
}: {
  title: ReactNode;
  /** Small overline naming the section (when there are no breadcrumbs). */
  eyebrow?: ReactNode;
  description?: ReactNode;
  breadcrumbs?: readonly Crumb[];
  actions?: ReactNode;
  /** Inline next to the title (status badge, id). */
  meta?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <header className={cn('flex flex-col gap-1 px-4 pt-4 pb-3', className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-1">
          <ol className="flex items-center gap-1 text-xs text-fg-subtle">
            {breadcrumbs.map((crumb, i) => (
              <li key={i} className="flex items-center gap-1">
                {crumb.to ? (
                  <Link to={crumb.to} className="rounded-xs hover:text-fg">
                    {crumb.label}
                  </Link>
                ) : (
                  <span aria-current="page">{crumb.label}</span>
                )}
                {i < breadcrumbs.length - 1 && <ChevronRight aria-hidden className="size-3 text-fg-faint" />}
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-col gap-1">
          {eyebrow && <p className="micro-label">{eyebrow}</p>}
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h1 className="truncate text-lg font-semibold text-fg">{title}</h1>
            {meta}
          </div>
          {description && <p className="max-w-3xl text-base text-fg-muted">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

/** Filter/search row above a table. */
export function Toolbar({
  children,
  className,
  end,
}: {
  children?: ReactNode;
  className?: string;
  end?: ReactNode;
}) {
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-2 px-4 pb-3', className)}>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      {end && <div className="flex items-center gap-2">{end}</div>}
    </div>
  );
}

/** Section heading inside a page body. */
export function SectionTitle({
  children,
  actions,
  className,
}: {
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between gap-2', className)}>
      <h2 className="text-base font-semibold text-fg">{children}</h2>
      {actions && <div className="flex items-center gap-1.5">{actions}</div>}
    </div>
  );
}
